import { useEffect, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, BarChart3, ChevronDown, ChevronRight, Pencil, Clock, Trash2 } from "lucide-react";
import { getApiUrl } from "@/utils/api";
import EditOrderDialog from "@/components/orders/EditOrderDialog";
import OrdersBySupplierAdmin from "@/components/admin/OrdersBySupplierAdmin";

const API = getApiUrl();

export default function ReportsAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [unpaid, setUnpaid] = useState([]);
  const [unpaidLoading, setUnpaidLoading] = useState(true);
  const [hours, setHours] = useState(24);
  const [editingOrder, setEditingOrder] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/reports/product-sales-by-client`, { withCredentials: true });
      setRows(data.rows || []);
    } catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };

  // Amend an unpaid-expired order's status inline. If the new status is
  // "paid" the row naturally drops out of this list (server filter), so we
  // refetch instead of trying to mutate in place.
  const updateStatus = async (order_id, next) => {
    const prev = unpaid;
    setUnpaid((os) => os.map((o) => (o.order_id === order_id ? { ...o, payment_status: next } : o)));
    try {
      await axios.put(`${API}/admin/orders/${order_id}/status`, { status: next }, { withCredentials: true });
      toast.success(`Status updated to ${next}`);
      if (next === "paid") await loadUnpaid();
    } catch (e) {
      setUnpaid(prev);
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  const loadUnpaid = async (h = hours) => {
    try {
      setUnpaidLoading(true);
      const { data } = await axios.get(
        `${API}/admin/orders/unpaid-expired?hours=${h}`,
        { withCredentials: true }
      );
      setUnpaid(data.orders || []);
    } catch { toast.error("Failed to load unpaid orders"); }
    finally { setUnpaidLoading(false); }
  };

  useEffect(() => { load(); loadUnpaid(); }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const totalSpent = rows.reduce((s, r) => s + Number(r.total_spent || 0), 0);
  const currency = rows[0]?.currency || "GBP";

  return (
    <div className="space-y-6" data-testid="reports-admin">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2"><BarChart3 className="h-6 w-6" /> Reports</h2>
        <p className="text-sm text-muted-foreground mt-1">Insights across your shop.</p>
      </div>

      <section className="border border-border rounded bg-background">
        <header className="px-4 py-3 border-b bg-muted/40 flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Product sales per client</h3>
            <p className="text-xs text-muted-foreground">Every paid order rolled up by unique customer email. Click a row to see the products they bought.</p>
          </div>
          <div className="text-right text-sm">
            <div className="text-muted-foreground text-xs">Total revenue</div>
            <div className="font-serif text-lg">{currency} {totalSpent.toFixed(2)}</div>
          </div>
        </header>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No paid orders yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground text-xs">
              <tr><th className="p-3 w-6"></th><th className="p-3">Client</th><th className="p-3">Email</th><th className="p-3">Phone</th><th className="p-3">Orders</th><th className="p-3 text-right">Total spent</th></tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const isOpen = !!expanded[r.email];
                return (
                  <Fragment key={r.email + idx}>
                    <tr className="border-t border-border cursor-pointer hover:bg-muted/30" onClick={() => setExpanded((p) => ({ ...p, [r.email]: !p[r.email] }))} data-testid={`report-row-${idx}`}>
                      <td className="p-3">{isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}</td>
                      <td className="p-3 font-medium">{r.name || "—"}</td>
                      <td className="p-3">{r.email}</td>
                      <td className="p-3">{r.phone || "—"}</td>
                      <td className="p-3">{r.orders_count}</td>
                      <td className="p-3 text-right whitespace-nowrap">{r.currency} {Number(r.total_spent).toFixed(2)}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-muted/20">
                        <td></td>
                        <td colSpan={5} className="p-3">
                          <table className="w-full text-xs">
                            <thead className="text-muted-foreground"><tr><th className="text-left py-1">Product</th><th className="text-left py-1">Quantity</th><th className="text-right py-1">Subtotal</th></tr></thead>
                            <tbody>
                              {r.items.map((it) => (
                                <tr key={(it.product_id || it.name) + idx} className="border-t border-border/60">
                                  <td className="py-1">
                                    {it.name}
                                    {it.is_plan && (
                                      <span
                                        className="ml-2 text-[9px] px-1 py-0.5 rounded bg-indigo-100 text-indigo-700 align-middle"
                                        data-testid={`report-item-plan-badge-${it.product_id || it.name}`}
                                      >
                                        Plan
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-1">{it.quantity}</td>
                                  <td className="py-1 text-right whitespace-nowrap">{r.currency} {Number(it.subtotal).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <OrdersBySupplierAdmin />

      <section className="bg-white border border-border p-4" data-testid="unpaid-expired-section">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <h3 className="font-serif text-lg font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" /> Unpaid expired orders
            </h3>
            <p className="text-xs text-muted-foreground">
              Checkouts older than the threshold that never completed payment.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <label className="text-muted-foreground">Older than</label>
            <select
              value={hours}
              onChange={(e) => { const h = Number(e.target.value); setHours(h); loadUnpaid(h); }}
              className="text-xs border border-input rounded-none px-2 py-1 bg-background"
              data-testid="unpaid-expired-hours"
            >
              {[1, 6, 12, 24, 48, 72, 168].map((h) => (
                <option key={h} value={h}>{h}h</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => loadUnpaid()}
              className="text-primary hover:underline"
              data-testid="unpaid-expired-refresh"
            >
              Refresh
            </button>
          </div>
        </div>
        {unpaidLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : unpaid.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-6" data-testid="unpaid-expired-empty">
            No unpaid orders older than {hours}h.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide bg-muted/40">
              <tr>
                <th className="p-2">Placed</th>
                <th className="p-2">Order</th>
                <th className="p-2">Customer</th>
                <th className="p-2">Property</th>
                <th className="p-2">Items</th>
                <th className="p-2">Total</th>
                <th className="p-2">Status</th>
                <th className="p-2 text-right">Manage</th>
              </tr>
            </thead>
            <tbody>
              {unpaid.map((o) => (
                <tr key={o.order_id} className="border-t border-border align-top" data-testid={`unpaid-order-row-${o.order_id}`}>
                  <td className="p-2 text-xs whitespace-nowrap">{o.created_at ? new Date(o.created_at).toLocaleString() : "—"}</td>
                  <td className="p-2 font-mono text-xs">#{(o.order_id || "").slice(0, 8).toUpperCase()}</td>
                  <td className="p-2 text-xs">
                    <div className="font-medium">{o.customer?.name || "—"}</div>
                    <div className="text-muted-foreground truncate max-w-[220px]">{o.customer?.email || ""}</div>
                  </td>
                  <td className="p-2">
                    {o.assigned_property ? (
                      <Link
                        to={`/property/${o.assigned_property.property_id}`}
                        className="flex items-center gap-2 group"
                        data-testid={`unpaid-order-property-${o.order_id}`}
                        title={o.assigned_property.title}
                      >
                        {o.assigned_property.main_image ? (
                          <img
                            src={o.assigned_property.main_image}
                            alt=""
                            className="h-10 w-14 object-cover rounded border border-border shrink-0"
                            loading="lazy"
                          />
                        ) : (
                          <div className="h-10 w-14 bg-muted border border-border rounded shrink-0" />
                        )}
                        <span className="text-xs font-medium text-primary group-hover:underline line-clamp-2 max-w-[160px]">
                          {o.assigned_property.title || "Untitled property"}
                        </span>
                      </Link>
                    ) : o.is_plan ? (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700"
                        data-testid={`unpaid-order-plan-badge-${o.order_id}`}
                        title="Account-level agent plan subscription — not tied to a property"
                      >
                        Plan purchase
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="p-2 text-xs">
                    {(o.items || []).map((it, i) => (
                      <div key={i}>{it.quantity} × {it.name}</div>
                    ))}
                  </td>
                  <td className="p-2 whitespace-nowrap font-medium text-sm">
                    {({ GBP: "£", USD: "$", EUR: "€" }[o.currency] || "")}{Number(o.total || 0).toFixed(2)}
                  </td>
                  <td className="p-2">
                    {(() => {
                      const cls = {
                        paid: "bg-green-100 text-green-700 border-green-200",
                        pending: "bg-amber-100 text-amber-700 border-amber-200",
                        failed: "bg-red-100 text-red-700 border-red-200",
                        refunded: "bg-blue-100 text-blue-700 border-blue-200",
                        cancelled: "bg-gray-200 text-gray-700 border-gray-300",
                        test: "bg-purple-100 text-purple-700 border-purple-200",
                        void: "bg-slate-200 text-slate-600 border-slate-300",
                      }[o.payment_status] || "bg-muted text-foreground border-border";
                      return (
                        <select
                          value={o.payment_status || "pending"}
                          onChange={(e) => updateStatus(o.order_id, e.target.value)}
                          className={`text-xs px-2 py-1 rounded border cursor-pointer ${cls}`}
                          data-testid={`unpaid-order-status-${o.order_id}`}
                          aria-label="Order status"
                        >
                          <option value="pending">Pending</option>
                          <option value="paid">Paid</option>
                          <option value="failed">Failed</option>
                          <option value="refunded">Refunded</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="test">Test</option>
                          <option value="void">Void</option>
                        </select>
                      );
                    })()}
                  </td>
                  <td className="p-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingOrder(o)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                        data-testid={`unpaid-order-manage-${o.order_id}`}
                      >
                        <Pencil className="h-3 w-3" /> Manage
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm("Delete this order? This cannot be undone.")) return;
                          try {
                            await axios.delete(`${API}/admin/orders/${o.order_id}`, { withCredentials: true });
                            toast.success("Order deleted");
                            await loadUnpaid();
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Failed to delete order");
                          }
                        }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:underline"
                        data-testid={`unpaid-order-delete-${o.order_id}`}
                        title="Delete order"
                      >
                        <Trash2 className="h-3 w-3" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <EditOrderDialog
        open={!!editingOrder}
        onOpenChange={(o) => { if (!o) setEditingOrder(null); }}
        order={editingOrder}
        apiBase="/admin/orders"
        onSaved={() => loadUnpaid()}
        onDeleted={() => loadUnpaid()}
      />
    </div>
  );
}
